/* Publishing Style Panel — Feb-2026.
 *
 * Author-facing font + layout picker. Persists the selection to
 * `projects.publishing_style` via the backend, so every published
 * reader (bookstore, EPUB, KDP PDF, in-house, child companion)
 * inherits the same typography.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Loader2, X, Type } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const ax  = axios.create({ baseURL: API, withCredentials: true });

export default function PublishingStylePanel({ projectId, onSaved, onClose }) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState(null);
  const [style,   setStyle]   = useState(null);
  const [busy,    setBusy]    = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [c, s] = await Promise.all([
        ax.get('/publishing-style/catalog'),
        projectId ? ax.get(`/projects/${projectId}/publishing-style`) : Promise.resolve({ data: {} }),
      ]);
      setCatalog(c.data);
      setStyle(s.data?.style || c.data.defaults);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('publish.styleLoadFailed'));
    } finally { setBusy(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const patch = (k, v) => setStyle((cur) => ({ ...(cur || {}), [k]: v }));

  const save = async () => {
    if (!projectId) { toast.error(t('publish.styleNoProject')); return; }
    setBusy(true);
    try {
      const r = await ax.put(`/projects/${projectId}/publishing-style`, style || {});
      setStyle(r.data.style);
      toast.success(t('publish.styleSaved'));
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('publish.styleSaveFailed'));
    } finally { setBusy(false); }
  };

  if (!catalog || !style) {
    return (
      <div className="mb-4 rounded-md border border-border bg-card p-3 text-[13.5px] text-muted-foreground"
           data-testid="pub-style-loading">
        <Loader2 className="mr-1 inline-block h-3.5 w-3.5 animate-spin" /> {t('publish.styleLoading')}
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-md border border-border bg-card p-4"
         data-testid="publishing-style-panel">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] font-semibold">
          <Type className="h-4 w-4" /> {t('publish.styleTitle')}
        </div>
        {onClose && (
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"
                  data-testid="pub-style-close-btn">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-[13px] uppercase tracking-wider text-muted-foreground">
            {t('publish.styleBodyFont')}
          </label>
          <select value={style.body_font}
                  onChange={(e) => patch('body_font', e.target.value)}
                  data-testid="pub-style-body-font"
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[14px]">
            {catalog.body_fonts.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[13px] uppercase tracking-wider text-muted-foreground">
            {t('publish.styleChapterFont')}
          </label>
          <select value={style.chapter_font}
                  onChange={(e) => patch('chapter_font', e.target.value)}
                  data-testid="pub-style-chapter-font"
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[14px]">
            {catalog.chapter_fonts.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[13px] uppercase tracking-wider text-muted-foreground">
            {t('publish.styleTrimSize')}
          </label>
          <select value={style.trim_size}
                  onChange={(e) => patch('trim_size', e.target.value)}
                  data-testid="pub-style-trim-size"
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[14px]">
            {catalog.trim_sizes.map((t2) => (
              <option key={t2.id} value={t2.id}>{t2.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[13px] uppercase tracking-wider text-muted-foreground">
            {t('publish.styleLineSpacing')}
          </label>
          <input type="number" step="0.05" min="1" max="2.5"
                 value={style.leading}
                 onChange={(e) => patch('leading', parseFloat(e.target.value) || 1.35)}
                 data-testid="pub-style-leading"
                 className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[14px]" />
        </div>
        <div>
          <label className="mb-1 block text-[13px] uppercase tracking-wider text-muted-foreground">
            {t('publish.styleFirstLineIndent')}
          </label>
          <input type="number" step="0.05" min="0" max="1.5"
                 value={style.indent_in}
                 onChange={(e) => patch('indent_in', parseFloat(e.target.value) || 0.35)}
                 data-testid="pub-style-indent"
                 className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[14px]" />
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-[14px]">
            <input type="checkbox" checked={!!style.drop_cap}
                   onChange={(e) => patch('drop_cap', e.target.checked)}
                   data-testid="pub-style-drop-cap" />
            {t('publish.styleDropCap')}
          </label>
          <label className="flex items-center gap-2 text-[14px]">
            <input type="checkbox" checked={!!style.mirror_margins}
                   onChange={(e) => patch('mirror_margins', e.target.checked)}
                   data-testid="pub-style-mirror-margins" />
            {t('publish.styleMirrorMargins')}
          </label>
        </div>
      </div>

      {/* Preview */}
      <div className="mt-4 rounded-md border border-dashed border-border bg-secondary/20 p-4"
           data-testid="pub-style-preview">
        <div className="mb-1 text-[9px] uppercase tracking-[0.24em] text-muted-foreground">{t('publish.stylePreview')}</div>
        <div style={{
          fontFamily: catalog.body_fonts.find((f) => f.id === style.body_font)?.stack,
          fontSize: `${style.body_size_pt || 11}pt`,
          lineHeight: style.leading,
        }}>
          <h3 style={{
            fontFamily: catalog.chapter_fonts.find((f) => f.id === style.chapter_font)?.stack === 'inherit'
              ? catalog.body_fonts.find((f) => f.id === style.body_font)?.stack
              : catalog.chapter_fonts.find((f) => f.id === style.chapter_font)?.stack,
            fontSize: `${style.chapter_size_pt || 22}pt`,
            fontWeight: 700,
            textAlign: 'center',
            margin: '0 0 1em 0',
          }}>
            {t('publish.styleChapterOne')}
          </h3>
          <p style={{ textIndent: 0, margin: 0 }}>
            {style.drop_cap && <span style={{
              float: 'left', fontSize: '3.2em', lineHeight: 0.85, fontWeight: 700,
              padding: '0.1em 0.12em 0 0',
            }}>T</span>}
            {!style.drop_cap && 'T'}he morning light fell across the ledger, cold and clean, and she
            thought again of the letter she had not yet written.
          </p>
          <p style={{ textIndent: `${style.indent_in}in`, margin: 0 }}>
            The house was quiet. The clock on the mantle held its tongue as though it, too, were
            waiting for her to begin.
          </p>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onClose}
                data-testid="pub-style-cancel-btn">
          {t('publish.styleCancel')}
        </Button>
        <Button size="sm" onClick={save} disabled={busy}
                data-testid="pub-style-save-btn">
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
          {t('publish.styleSave')}
        </Button>
      </div>
    </div>
  );
}
